import {useEffect, useRef} from 'react'
import {useConnectionState, useLocalParticipant} from '@livekit/components-react'
import {ConnectionState, Track} from 'livekit-client'
import type {LocalTrackPublication, TrackPublication} from 'livekit-client'
import type {NoiseGateProcessor} from './noiseGateProcessor'

/**
 * Applies the noise gate processor to the local microphone track.
 * Must be used inside LiveKitRoom. Applies on connect and re-applies after mute/unmute.
 */
export function useApplyNoiseGateToLocalMic(processor: NoiseGateProcessor) {
  const connectionState = useConnectionState()
  const {localParticipant} = useLocalParticipant()
  const isConnected = connectionState === ConnectionState.Connected
  const processorAppliedRef = useRef(false)

  // Apply processor when mic track becomes available on initial connect
  useEffect(() => {
    if (!isConnected || !localParticipant || processorAppliedRef.current) return

    const micPub = localParticipant.getTrackPublication(Track.Source.Microphone)
    const audioTrack = micPub?.audioTrack

    if (audioTrack) {
      audioTrack.setProcessor(processor).catch(err => {
        console.warn('Failed to set noise gate processor:', err)
      })
      processorAppliedRef.current = true
    }
  }, [isConnected, localParticipant, processor])

  // Re-apply processor when mic track is re-published or unmuted.
  //
  // Two cases are covered:
  //   1. setMicrophoneEnabled(false→true) causes LiveKit to unpublish + republish
  //      the track — handled via 'localTrackPublished'.
  //   2. On some LiveKit versions mute/unmute keeps the same publication and calls
  //      processor.restart() internally, but may not re-attach if restart fails —
  //      'trackUnmuted' acts as a safety net in that case.
  useEffect(() => {
    if (!isConnected || !localParticipant) return

    const tryApplyProcessor = (pub: LocalTrackPublication) => {
      if (pub.source !== Track.Source.Microphone) return
      const audioTrack = pub.audioTrack
      if (!audioTrack) return
      // Guard against double-applying: only set if the track has no processor yet.
      if (!audioTrack.getProcessor()) {
        audioTrack.setProcessor(processor).catch(err => {
          console.warn('Failed to re-apply noise gate processor:', err)
        })
      }
    }

    // trackUnmuted passes TrackPublication (base type) — cast is safe for local tracks.
    const handleUnmuted = (pub: TrackPublication) => {
      tryApplyProcessor(pub as LocalTrackPublication)
    }

    localParticipant.on('localTrackPublished', tryApplyProcessor)
    localParticipant.on('trackUnmuted', handleUnmuted)
    return () => {
      localParticipant.off('localTrackPublished', tryApplyProcessor)
      localParticipant.off('trackUnmuted', handleUnmuted)
    }
  }, [isConnected, localParticipant, processor])
}
